import { useRef, useReducer, useState, useEffect, useMemo } from "react";
import {
  DateBox,
  SelectBox,
  ScrollView,
  Button,
  NumberBox,
  TextBox,
  Validator,
} from "devextreme-react";
import { CustomRule } from "devextreme-react/validator";
import moment from "moment";
import { Popup, Position } from "devextreme-react/popup";
import { sortBy } from "lodash";

import * as actions from "../../../actions/schAttainment";
import schAttainmentReducer from "../../../reducers/schAttainment";
import { alert } from "devextreme/ui/dialog";
import ReasonCodeEntry from "./ReasonCodeEntry";
import { postUserLog } from "../../../actions/common";

const initState = {
  schAttainmentReasonCodes: [],
  dailyInputDetails: null,
  shiftDetails: null,
  refreshSchAttainment: null,
};

const DailyInput = (props) => {
  const {
    open,
    close,
    tierId,
    isRefreshDataRequired,
    setIsRefreshDataRequired,
    siteId,
    navigationOn,
  } = props;

  const [state, dispatch] = useReducer(schAttainmentReducer, initState);

  const [selectedShift, setSelectedShift] = useState(1);
  const [schAttainmentDate, setSchAttainemntDate] = useState(new Date());
  const [onTimePlannedOrders, setOnTimePlannedOrders] = useState(null);
  const [totalPlannedOrders, setTotalPlannedOrders] = useState(null);
  const [shiftData, setShiftData] = useState([]);
  const [reasons, setReasons] = useState([]);
  const schAttainmentReasonGrid = useRef();
  const [saReasonData, setsaReasonData] = useState([]);

  const {
    schAttainmentReasonCodes,
    shiftDetails,
    dailyInputDetails,
    refreshSchAttainment,
  } = state;

  const shiftList = useMemo(() => {
    const shifts = [];
    for (let i = 0; i < shiftDetails?.shift; i++) {
      shifts.push({
        label: `Shift ${i + 1}`,
        value: i + 1,
      });
    }
    return shifts;
  }, [shiftDetails]);

  useEffect(() => {
    actions.fetchDailyInputDetails(
      dispatch,
      tierId,
      selectedShift,
      moment(schAttainmentDate).format("YYYY-MM-DD")
    );
    actions.fetchDailyInputTarget(
      dispatch,
      `${tierId}&type=SA&date=${moment(schAttainmentDate).format("YYYY-MM-DD")}`
    );
    actions.fetchReasonCodes(dispatch, tierId);
    postUserLog({
      event: "Daily Input Screen Launch",
      data: {
        action: "Sched. Attainment Daily Input Launched",
        module: "Digital Tier",
        section: navigationOn,
        siteUId: siteId,
        tierId: tierId,
        widget: "Delivery Metric: Schedule Attainment",
      },
    });
  }, [schAttainmentDate]);

  useEffect(() => {
    // Calculate the percentage and color for initial load
    if (dailyInputDetails !== null && shiftDetails !== null) {
      setShiftData(calculateInitialData());
      setPlannedAndOnTimeOrders();
    }
  }, [dailyInputDetails, shiftDetails]);

  useEffect(() => {
    // Set onTimePlannedOrders and TotalPlannedOrders to input Boxes
    if (totalPlannedOrders !== null && onTimePlannedOrders !== null) {
      calculateSchAttainment(onTimePlannedOrders, totalPlannedOrders);
    }
  }, [totalPlannedOrders, onTimePlannedOrders]);

  useEffect(() => {
    if (selectedShift) {
      setShiftData(calculateInitialData());
      setPlannedAndOnTimeOrders();
    }
  }, [selectedShift]);

  useEffect(() => {
    if (refreshSchAttainment) {
      close(false);
      setIsRefreshDataRequired(!isRefreshDataRequired);
    }
  }, [refreshSchAttainment]);

  const setPlannedAndOnTimeOrders = () => {
    const activeShiftInfo = calculateInitialData().find(
      (shift) => shift?.shift === selectedShift
    );
    activeShiftInfo?.onTimePlannedOrders
      ? setOnTimePlannedOrders(() => activeShiftInfo?.onTimePlannedOrders)
      : setOnTimePlannedOrders(() => null);
    activeShiftInfo?.totalPlannedOrders
      ? setTotalPlannedOrders(() => activeShiftInfo?.totalPlannedOrders)
      : setTotalPlannedOrders(() => null);
    activeShiftInfo?.reasons &&
    activeShiftInfo?.reasons[0]?.qty !== 0 &&
    activeShiftInfo?.reasons[0]?.reasonCodeId !== 0
      ? setReasons(() => activeShiftInfo?.reasons)
      : setReasons([]);
  };

  const calculateSchAttainment = (onTimePlannedOrders, totalPlannedOrders) => {
    if (onTimePlannedOrders >= 0 && totalPlannedOrders > 0) {
      const schAttainmentPercent =
        (onTimePlannedOrders / totalPlannedOrders) * 100;

      const currentShiftData = [];

      shiftData.map((shift) => {
        if (shift?.shift === selectedShift) {
          shift["andOnColor"] =
            shiftDetails?.target <= schAttainmentPercent
              ? "#48ff00"
              : "#ff0000";
          shift["schAttainmentPercent"] = schAttainmentPercent;
        }
        currentShiftData.push(shift);
      });
      setShiftData(currentShiftData);
    }
  };

  const calculateInitialData = () => {
    const calculatedShiftData = [];
    const tempDailyInputDetails = dailyInputDetails
      ? structuredClone(dailyInputDetails)
      : []; //works only after NODE 17.0

    if (tempDailyInputDetails.length === shiftDetails?.shift) {
      tempDailyInputDetails?.map((shift) => {
        if (shift.onTimePlannedOrders >= 0 && shift.totalPlannedOrders > 0) {
          const schAttainmentPercent =
            (shift.onTimePlannedOrders / shift.totalPlannedOrders) * 100;
          shift["andOnColor"] =
            shiftDetails?.target <= schAttainmentPercent
              ? "#48ff00"
              : "#ff0000";
          shift["schAttainmentPercent"] = schAttainmentPercent;
          return calculatedShiftData.push(shift);
        }
        return calculatedShiftData.push(shift);
      });
    } else {
      for (let i = 0; i < shiftDetails?.shift; i++) {
        const shiftData = tempDailyInputDetails.find(
          (shift) => shift?.shift === i + 1
        );
        if (shiftData) {
          if (
            shiftData.onTimePlannedOrders >= 0 &&
            shiftData.totalPlannedOrders > 0
          ) {
            const schAttainmentPercent =
              (shiftData.onTimePlannedOrders / shiftData.totalPlannedOrders) *
              100;
            shiftData["andOnColor"] =
              shiftDetails?.target <= schAttainmentPercent
                ? "#48ff00"
                : "#ff0000";
            shiftData["schAttainmentPercent"] = schAttainmentPercent;
            calculatedShiftData.push(shiftData);
          } else {
            calculatedShiftData.push(shiftData);
          }
        } else {
          calculatedShiftData.push({
            shift: i + 1,
            id: 0,
          });
        }
      }
    }
    return sortBy(calculatedShiftData, ["shift"]);
  };

  const validateTotalPlannedOrders = (e) => {
    if (e.value != null && onTimePlannedOrders != null) {
      return e?.value >= onTimePlannedOrders;
    }
  };

  const onSave = () => {
    if (selectedShift < 1) {
      alert("Please select a Shift.", "Alert");
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for Please select a Shift. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    if (!onTimePlannedOrders) {
      alert("Please enter on time planned orders.", "Alert");
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for Please enter on time planned orders. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    if (!(onTimePlannedOrders >= 0)) {
      alert(
        "On time planned orders must be greater than or equal to 0.",
        "Alert"
      );
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for On time planned orders must be greater than or equal to 0. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    if (!totalPlannedOrders) {
      alert("Please enter total planned orders.", "Alert");
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for Please enter total planned orders. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    if (!(totalPlannedOrders > 0)) {
      alert("Total Planned Orders Must be greater than 0.", "Alert");
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for Total Planned Orders Must be greater than 0. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    if (!(totalPlannedOrders >= onTimePlannedOrders)) {
      alert(
        "On-Time Planned Orders cannot exceed Total Planned Orders.",
        "Alert"
      );
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for On-Time Planned Orders cannot exceed Total Planned Orders. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }

    if (!schAttainmentDate) {
      alert("Please select date.", "Alert");
      postUserLog({
        event: "Daily Input Screen Alert Message",
        data: {
          action: `Ok clicked for Please select date. pop-up`,
          module: "Digital Tier",
          section: navigationOn,
          siteUId: siteId,
          tierId: tierId,
          widget: "Delivery Metric: Schedule Attainment",
        },
      });
      return;
    }
    let reasons = [],
      isReasonValid = true;

    const schAttainmentPercent =
      (onTimePlannedOrders / totalPlannedOrders) * 100;
    if (schAttainmentPercent < shiftDetails?.target) {
      // let reasonData =
      // schAttainmentReasonGrid.current.instance.getVisibleRows();

      saReasonData.map((v) => {
        let d = v;
        let id = d.reasonCodeId ? d.reasonCodeId : null;
        let qty = d.qty ? d.qty : null;
        let reasonId = d.id ? d.id : 0;
        if (id && qty)
          reasons.push({
            id: reasonId,
            reasonCodeId: id,
            comment: d.comment ? d.comment : "",
            qty: qty,
          });
        else {
          isReasonValid = false;
          alert("Please fill all mandatory fields.", "Alert");
          postUserLog({
            event: "Daily Input Screen Alert Message",
            data: {
              action: `Ok clicked for Please fill all mandatory fields. pop-up`,
              module: "Digital Tier",
              section: navigationOn,
              siteUId: siteId,
              tierId: tierId,
              widget: "Delivery Metric: Schedule Attainment",
            },
          });
          return;
        }
      });

      if (saReasonData.length < 1) {
        alert(
          "No reason was mentioned for why Schedule Attainment % didn't meet the target. Please add reason.",
          "Alert"
        );
        postUserLog({
          event: "Daily Input Screen Alert Message",
          data: {
            action: `Ok clicked for No reason was mentioned for why Schedule Attainment % didn't meet the target. Please add reason. pop-up`,
            module: "Digital Tier",
            section: navigationOn,
            siteUId: siteId,
            tierId: tierId,
            widget: "Delivery Metric: Schedule Attainment",
          },
        });
        isReasonValid = false;
        return;
      }
      if (
        totalPlannedOrders - onTimePlannedOrders !==
        saReasonData.reduce((accum, item) => accum + item.qty, 0)
      ) {
        alert("Missed Quantity does not match.", "Alert");
        postUserLog({
          event: "Daily Input Screen Alert Message",
          data: {
            action: `Ok clicked for Missed Quantity does not match. pop-up`,
            module: "Digital Tier",
            section: navigationOn,
            siteUId: siteId,
            tierId: tierId,
            widget: "Delivery Metric: Schedule Attainment",
          },
        });
        isReasonValid = false;
      }
    }
    if (!isReasonValid) return;
    const payload = {
      id:
        dailyInputDetails?.find((shift) => shift?.shift === selectedShift) &&
        dailyInputDetails?.find((shift) => shift?.shift === selectedShift).id >
          0
          ? dailyInputDetails?.find((shift) => shift?.shift === selectedShift)
              .id
          : 0,
      tierId,
      target: 0,
      shift: selectedShift,
      date: moment(schAttainmentDate).format("YYYY-MM-DD"),
      onTimePlannedOrders,
      totalPlannedOrders,
      // userid: "string",
      prodHour: 0,
      reasons,
      section: navigationOn,
    };

    actions.addDailyInput(dispatch, payload);
  };

  function reasonCodeDisp(item) {
    return item && `${item?.level0} - ${item?.value}`;
  }

  return (
    <Popup
      visible={open}
      hideOnOutsideClick={false}
      title="Sch. Attainment"
      dragEnabled={false}
      width={980}
      height={600}
      onHiding={() => {
        close(false);
        postUserLog({
          event: "Daily Input Screen Exit",
          data: {
            action: "Exit Daily Input Screen",
            module: "Digital Tier",
            section: navigationOn,
            siteUId: siteId,
            tierId: tierId,
            widget: "Delivery Metric: Schedule Attainment",
          },
        });
      }}
    >
      <ScrollView width="100%" height="95%">
        <Position at="center" my="center" />
        <div>
          {shiftDetails?.target ? (
            <div>
              <div>
                <div className="flx-spc-btn">
                  <div>
                    <SelectBox
                      dataSource={shiftList}
                      valueExpr="value"
                      displayExpr="label"
                      placeholder="Select Shift"
                      label="Shift"
                      labelMode="floating"
                      value={selectedShift}
                      onValueChanged={(e) => setSelectedShift(e.value)}
                    />
                  </div>
                  <div>
                    <DateBox
                      label="Date"
                      labelMode="floating"
                      placeholder="Select Date"
                      type="date"
                      useMaskBehavior={true}
                      value={schAttainmentDate}
                      onValueChanged={(e) => setSchAttainemntDate(e.value)}
                      min={
                        shiftDetails?.initialDate
                          ? new Date(shiftDetails?.initialDate)
                          : new Date()
                      }
                      max={new Date()}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "end",
                    }}
                  >
                    <b
                      style={{
                        marginRight: "10px",
                      }}
                    >
                      Sch. Attainment Target:{" "}
                    </b>
                    <TextBox
                      readOnly={true}
                      value={`${shiftDetails?.target} %`}
                      width="25%"
                      inputAttr={{
                        id: "disabled-sch-attainment",
                      }}
                    />
                  </div>
                </div>
                <div className="flx-spc-btn" style={{ marginTop: "10px" }}>
                  <div className="dx-field" style={{ width: "38%" }}>
                    <NumberBox
                      className="dx-field-value"
                      label="On-Time Planned Orders *"
                      labelMode="floating"
                      placeholder="On-Time Planned Orders"
                      format="#0.##"
                      min={0}
                      value={onTimePlannedOrders}
                      onValueChanged={(e) => setOnTimePlannedOrders(e.value)}
                    >
                      <Validator>
                        {/* <RequiredRule message="On-Time Planned Orders is required" /> */}
                      </Validator>
                    </NumberBox>
                  </div>
                  <div
                    className="dx-field"
                    style={{ width: "38%", marginLeft: "14px" }}
                  >
                    <NumberBox
                      className="dx-field-value"
                      label="Total Planned Orders *"
                      labelMode="floating"
                      min={1}
                      format="#0.##"
                      value={totalPlannedOrders}
                      onValueChanged={(e) => {
                        setTotalPlannedOrders(e.value);
                      }}
                    >
                      <Validator>
                        {/* <RequiredRule message="Total Planned Orders is required" /> */}
                        <CustomRule
                          message="On-Time Planned Orders cannot exceed Total Planned Orders"
                          validationCallback={validateTotalPlannedOrders}
                        />
                      </Validator>
                    </NumberBox>
                  </div>

                  <div style={{ width: "60%" }}>
                    <>
                      {shiftData?.map((shift) => {
                        return (
                          <div className="dle-sadn">
                            <div>Shift {shift?.shift}</div>
                            <div>
                              <span
                                className="andon"
                                style={{
                                  background: shift?.andOnColor || "#666666",
                                }}
                              >
                                {" "}
                              </span>
                            </div>
                            <div style={{ width: "60px" }}>
                              {(shift?.schAttainmentPercent?.toFixed(2) ||
                                "0.00") + "%"}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  </div>
                </div>
              </div>
              <div>
                <ReasonCodeEntry
                  setReasonData={setsaReasonData}
                  tierId={tierId}
                  reasonsData={reasons}
                  onTimePlannedOrders={onTimePlannedOrders}
                  totalPlannedOrders={totalPlannedOrders}
                />
              </div>
              <div className="cbtn">
                <Button
                  // disabled={btnDisabled}
                  onClick={onSave}
                  text="Save"
                  type="normal"
                />
              </div>
            </div>
          ) : (
            shiftDetails?.target !== (null || undefined) && (
              <h3>Please have Tier Owner/Alternate set Target % first</h3>
            )
          )}
        </div>
      </ScrollView>
    </Popup>
  );
};

export default DailyInput;
